export type InstagramMediaType = 'post' | 'reel' | 'tv';

export interface ParsedInstagramUrl {
    shortCode: string;
    mediaType: InstagramMediaType;
    normalizedUrl: string;
    username?: string;
}

const INSTAGRAM_HOST_PATTERN = /^(?:(?:www|m)\.)?instagram\.com$|^instagr\.am$/i;
const SHORTCODE_PATTERN = /^[A-Za-z0-9_-]+$/;
const MEDIA_KINDS = new Set(['p', 'reel', 'reels', 'tv']);

const INSTAGRAM_HTTP_PATTERN = /https?:\/\/(?:www\.|m\.)?instagram\.com\/[^\s"'<>]+/gi;
const INSTAGRAM_PROTOCOLLESS_PATTERN = /(?:^|[\s(])((?:www\.|m\.)?instagram\.com\/[^\s"'<>]+)/gi;
const INSTAGRAM_DEEP_LINK_PATTERN = /instagram:\/\/[^\s"'<>]+/gi;
const INSTAGRAM_MEDIA_SNIPPET_PATTERN = /(?:instagram\.com|instagr\.am)\/(?:[\w.]+\/)?(reel|reels|p|tv)\/([A-Za-z0-9_-]+)/gi;

function mediaTypeFromKind(kind: string): InstagramMediaType | null {
    switch (kind.toLowerCase()) {
        case 'p':
            return 'post';
        case 'reel':
        case 'reels':
            return 'reel';
        case 'tv':
            return 'tv';
        default:
            return null;
    }
}

function pathPrefixForMediaType(mediaType: InstagramMediaType): string {
    if (mediaType === 'post') return 'p';
    if (mediaType === 'reel') return 'reel';
    return 'tv';
}

function buildNormalizedUrl(
    mediaType: InstagramMediaType,
    shortCode: string,
    username?: string,
): string {
    const pathPrefix = pathPrefixForMediaType(mediaType);
    return username
        ? `https://www.instagram.com/${username}/${pathPrefix}/${shortCode}/`
        : `https://www.instagram.com/${pathPrefix}/${shortCode}/`;
}

function parseInstagramPath(
    hostname: string,
    segments: string[],
): ParsedInstagramUrl | null {
    if (segments.length < 2) return null;

    let username: string | undefined;
    let kind: string;
    let rawShortCode: string;

    if (hostname === 'instagr.am') {
        if (segments[0].toLowerCase() !== 'p' || !segments[1]) return null;
        kind = 'p';
        rawShortCode = segments[1];
    } else if (MEDIA_KINDS.has(segments[0].toLowerCase())) {
        kind = segments[0];
        rawShortCode = segments[1];
    } else if (segments.length >= 3 && MEDIA_KINDS.has(segments[1].toLowerCase())) {
        username = segments[0];
        kind = segments[1];
        rawShortCode = segments[2];
    } else {
        return null;
    }

    const mediaType = mediaTypeFromKind(kind);
    if (!mediaType) return null;

    const shortCode = rawShortCode.split('?')[0];
    if (!SHORTCODE_PATTERN.test(shortCode)) return null;

    return {
        shortCode,
        mediaType,
        normalizedUrl: buildNormalizedUrl(mediaType, shortCode, username),
        username,
    };
}

function parseInstagramDeepLink(value: string): ParsedInstagramUrl | null {
    const trimmed = value.trim();
    const reelMatch = trimmed.match(/instagram:\/\/(?:reel\/|.*\/reel\/)([A-Za-z0-9_-]+)/i);
    if (reelMatch?.[1]) {
        return {
            shortCode: reelMatch[1],
            mediaType: 'reel',
            normalizedUrl: buildNormalizedUrl('reel', reelMatch[1]),
        };
    }

    const postMatch = trimmed.match(/instagram:\/\/(?:p\/|.*\/p\/)([A-Za-z0-9_-]+)/i);
    if (postMatch?.[1]) {
        return {
            shortCode: postMatch[1],
            mediaType: 'post',
            normalizedUrl: buildNormalizedUrl('post', postMatch[1]),
        };
    }

    const asHttps = trimmed.replace(/^instagram:\/\//i, 'https://');
    return parseInstagramUrl(asHttps);
}

export function isInstagramUrl(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed) return false;
    try {
        return tryParseInstagramReference(trimmed) !== null;
    } catch {
        return false;
    }
}

export function parseInstagramUrl(value: string): ParsedInstagramUrl | null {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^instagram:\/\//i.test(trimmed)) {
        return parseInstagramDeepLink(trimmed);
    }

    let candidate = trimmed;
    if (!/^https?:\/\//i.test(candidate) && /instagram\.com|instagr\.am/i.test(candidate)) {
        candidate = `https://${candidate.replace(/^\/\//, '')}`;
    }

    let parsed: URL;
    try {
        parsed = new URL(candidate);
    } catch {
        return null;
    }

    if (!INSTAGRAM_HOST_PATTERN.test(parsed.hostname)) {
        return null;
    }

    const segments = parsed.pathname.split('/').filter(Boolean);
    return parseInstagramPath(parsed.hostname.toLowerCase(), segments);
}

export function tryParseInstagramReference(value: string): ParsedInstagramUrl | null {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const direct = parseInstagramUrl(trimmed);
    if (direct) return direct;

    for (const match of trimmed.matchAll(INSTAGRAM_MEDIA_SNIPPET_PATTERN)) {
        const kind = match[1];
        const shortCode = match[2];
        const mediaType = mediaTypeFromKind(kind);
        if (!mediaType || !SHORTCODE_PATTERN.test(shortCode)) continue;
        return {
            shortCode,
            mediaType,
            normalizedUrl: buildNormalizedUrl(mediaType, shortCode),
        };
    }

    return null;
}

export function findInstagramMediaInText(text: string): ParsedInstagramUrl[] {
    const results: ParsedInstagramUrl[] = [];
    const seen = new Set<string>();

    const addCandidate = (candidate: string) => {
        const parsed = tryParseInstagramReference(candidate);
        if (!parsed || seen.has(parsed.normalizedUrl)) return;
        seen.add(parsed.normalizedUrl);
        results.push(parsed);
    };

    for (const match of text.match(INSTAGRAM_HTTP_PATTERN) ?? []) {
        addCandidate(match);
    }
    for (const match of text.match(INSTAGRAM_DEEP_LINK_PATTERN) ?? []) {
        addCandidate(match);
    }
    for (const match of text.matchAll(INSTAGRAM_PROTOCOLLESS_PATTERN)) {
        addCandidate(match[1]);
    }
    addCandidate(text);

    return results;
}

export function findBestInstagramMediaInShareFields(
    fields: readonly (string | undefined)[],
): ParsedInstagramUrl | null {
    let best: ParsedInstagramUrl | null = null;

    for (const field of fields) {
        if (typeof field !== 'string' || !field.trim()) continue;

        for (const parsed of findInstagramMediaInText(field)) {
            if (!best || parsed.normalizedUrl.length >= best.normalizedUrl.length) {
                best = parsed;
            }
        }
    }

    return best;
}
